import { ChatBubbleLeftIcon, TrashIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useMemo, useState } from 'react';

import { SearchBar } from '@/components/legacy-ui/SearchBar';
import { Conversation } from '@/types';
import { cn } from '@/utils/classname';

import { ModeBadge } from './components/ModeBadge';

interface ConversationListProps {
  conversations: Conversation[];
  activeConversationId?: string | null;
  onSelectConversation: (conversationId: string) => void;
  onDeleteConversation?: (conversationId: string) => void;
  conversationPreviews?: Record<string, string>;
  conversationModes?: Record<string, 'query' | 'agent' | 'auto'>;
}

function StatusBadge({ status }: { status?: Conversation['status'] }) {
  if (status === 'active') {
    return (
      <span className="relative ml-1.5 flex size-2" title="Active">
        <span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-400 opacity-75" />
        <span className="relative inline-flex size-2 rounded-full bg-emerald-500" />
      </span>
    );
  }
  return null;
}

const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  activeConversationId,
  onSelectConversation,
  onDeleteConversation,
  conversationPreviews = {},
  conversationModes = {},
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) {
      return 'Just now';
    } else if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Generate conversation title with name and commit-hash style UUID
  const getConversationTitle = useCallback(
    (conversation: Conversation) => {
      const conversationId =
        conversation.uuid ||
        conversation.id ||
        (conversation.key
          ? conversation.key.replace('#conversation#', '')
          : '');

      const shortId = conversationId ? conversationId.slice(-8) : 'unknown';

      const displayName =
        conversation.name ||
        conversation.title ||
        conversation.topic ||
        conversationPreviews[conversationId] ||
        'Chat';

      return `${displayName}@${shortId}`;
    },
    [conversationPreviews]
  );

  // Filter conversations based on search query
  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) return conversations;

    const query = searchQuery.toLowerCase();
    return conversations.filter(conversation => {
      const conversationId =
        conversation.uuid ||
        conversation.id ||
        (conversation.key
          ? conversation.key.replace('#conversation#', '')
          : '');

      const displayTitle = getConversationTitle(conversation);

      return (
        conversationId?.toLowerCase().includes(query) ||
        displayTitle?.toLowerCase().includes(query)
      );
    });
  }, [conversations, searchQuery, getConversationTitle]);

  const searchBox = (
    <div className="border-chariot-stroke border-b p-1 pb-2">
      <SearchBar
        size="sm"
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search conversations"
        className="w-full"
      />
    </div>
  );

  if (filteredConversations.length === 0 && searchQuery) {
    return (
      <div>
        {searchBox}
        <div className="p-6 text-center">
          <div className="text-chariot-text-secondary">
            <p className="text-sm">No conversations found</p>
            <p className="mt-1 text-xs opacity-75">
              Try a different search term
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="p-6 text-center">
        <div className="text-chariot-text-secondary">
          <ChatBubbleLeftIcon className="mx-auto mb-3 h-8 w-8 opacity-40" />
          <p className="text-sm">No conversations yet</p>
          <p className="mt-1 text-xs opacity-75">
            Start a new conversation to get started
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Search input */}
      {searchBox}

      {filteredConversations.map((conversation, index) => {
        const conversationId =
          conversation.uuid ||
          conversation.id ||
          (conversation.key
            ? conversation.key.replace('#conversation#', '')
            : '');
        const isActive = conversationId === activeConversationId;
        const isFirst = index === 0;

        return (
          <div
            key={conversationId || conversation.key}
            className={cn(
              'group flex items-center transition-all',
              'py-2 pr-1 pl-2 text-sm',
              'cursor-pointer focus-visible:outline-hidden',
              !isFirst && 'border-chariot-stroke border-t',
              isActive && 'bg-chariot-stroke text-chariot-text-primary',
              'hover:shadow-[0_0_6px_2px_rgba(0,0,0,0.35)] dark:hover:shadow-[0_0_6px_2px_rgba(255,255,255,0.35)]'
            )}
            tabIndex={0}
            onClick={() => onSelectConversation(conversationId)}
          >
            <div className="flex-1 truncate">
              <p
                className={cn(
                  'flex items-center gap-1.5 truncate text-xs font-medium',
                  isActive
                    ? 'text-chariot-text-primary'
                    : 'text-chariot-text-secondary'
                )}
              >
                <span className="truncate">
                  {conversation.topic ||
                    conversationPreviews[conversationId] ||
                    'New Conversation'}
                </span>
                <StatusBadge status={conversation.status} />
                {(() => {
                  const mode = conversation.huntId
                    ? ('auto' as const)
                    : (conversationModes[conversationId] ?? undefined);
                  return mode ? <ModeBadge mode={mode} /> : null;
                })()}
              </p>
              <p className="text-chariot-text-tertiary mt-1 truncate text-xs">
                {formatDate(conversation.created)}
              </p>
            </div>
            {onDeleteConversation && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  onDeleteConversation(conversationId);
                }}
                className="text-chariot-text-tertiary hover:text-chariot-stroke-critical shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
                title="Delete conversation"
              >
                <TrashIcon className="size-4" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ConversationList;
